<!-- Copyright 2026, Common Good Learning Tools LLC -->
<template><k-dialog v-model="dialog_open" fullscreen persistent scrollable>
<k-card>
	<k-card-title class="black white--text py-2" style="border-bottom:1px solid #999">
		<k-icon color="#fff" class="mr-3" style="font-size:30px;">fas fa-cubes</k-icon>
		<b>Satchel Competency-Resource Alignment System (SCRAM)</b>

		<k-spacer/>

		<k-btn color="#666" dark fab x-small accessible-label="Close resource alignment tool" @click="$emit('dialog_cancel')"><k-icon>fas fa-xmark</k-icon></k-btn>

	</k-card-title>
	<k-card-text class="k-scram-job-card" style="font-size:16px; line-height:19px"
		v-if='initialized'
	>

	<div class="mt-3 mb-5 d-flex align-center">
		<h3>Existing SCRAM Jobs:</h3>
		
		<k-spacer/>

		<k-btn color="primary"
			@click="create_new_job"
		><k-icon small class="mr-2">fas fa-plus</k-icon>Create New Job</k-btn>
	</div>

	<k-data-table light dense
		:headers="table_headers"
		:items="table_rows"
		:footer-props="footer_options"
		:items-per-page="25"
		class="k-rcram-jobs-table"
	>
		<!-- BODY ROWS -->
		<template v-slot:item="{ item }">
			<tr>
				<td class="k-rcram-manager-id-cell">{{item.cram_job_id}}</td>
				<td class="k-rcram-manager-description-cell">{{item.job_description}}</td>
				<td class="k-rcram-manager-creator-cell">{{item.creator_email}}</td>
				<td class="k-rcram-manager-count-cell">{{item.resourceCount}}</td>
				<td class="k-rcram-manager-count-cell">{{item.competencyCount}}</td>
				<td class="k-rcram-manager-count-cell">{{item.matrixCellCount}}</td>
				<td class="k-rcram-manager-status-cell">{{item.worker_status_string}}</td>
				<td class="k-rcram-manager-ai-cost-cell">{{item.ai_stats}}</td>
				<td class="k-rcram-manager-actions-cell">

					<v-tooltip bottom><template v-slot:activator="{on}">
						<k-btn v-on="on" small icon color="primary"
							@click="view_grid(item.cram_job_id)"
						><k-icon small>fas fa-grid</k-icon></k-btn>
					</template>Grid View</v-tooltip>
					
					<v-tooltip bottom><template v-slot:activator="{on}">
						<k-btn v-on="on" small icon color="primary"
							@click="view_alignment_report(item.cram_job_id)"
						><k-icon small>fas fa-list-radio</k-icon></k-btn>
					</template>Alignment Report</v-tooltip>

					<v-tooltip bottom><template v-slot:activator="{on}">
						<k-btn v-on="on" small icon color="primary"
							@click="edit_job(item.cram_job_id)"
						><k-icon small>fas fa-edit</k-icon></k-btn>
					</template>Edit</v-tooltip>

				</td>
			</tr>
		</template>

	</k-data-table>

	</k-card-text>
</k-card>
</k-dialog></template>

<script>
import { mapState, mapGetters } from 'vuex'

export default {
	components: {  },
	props: {
		scram_manager_update_id: { required: true },
	},
	data() { return {
		dialog_open: true,
		initialized: false,

		jobs: [],
		footer_options: {
			itemsPerPageOptions: [25,50,100],
		},
	}},
	computed: {
		...mapState(['site_config', 'framework_records', 'user_info']),
		...mapGetters([]),

		table_headers() {
			let arr = []
			arr.push({ text: 'ID', align:'center', sortable: true, value: 'cram_job_id' } )
			arr.push({ text: 'Description', align:'left', sortable: true, value: 'job_description' } )
			arr.push({ text: 'Creator', align:'center', sortable: true, value: 'creator_email' } )
			arr.push({ text: 'Resources', align:'center', sortable: true, value: 'resourceCount' } )
			arr.push({ text: 'Comps', align:'center', sortable: true, value: 'competencyCount' } )
			arr.push({ text: 'Cells', align:'center', sortable: true, value: 'matrixCellCount' } )
			arr.push({ text: 'Job Status', align:'center', sortable: true, value: 'worker_status_string' } )
			arr.push({ text: 'GenAI Cost', align:'center', sortable: true, value: 'token_cost' } )
			arr.push({ text: 'Actions', align:'center', sortable: false, value: 'cram_job_id' } )

			return arr
		},

		table_rows() {
			let arr = []
			for (let job of this.jobs) {
				let jo = {}
				jo.job = job
				jo.cram_job_id = job.cram_job_id
				jo.job_description = job.job_description ?? ''
				jo.creator_email = job.creator_email
				jo.worker_status = job.worker_status
				jo.token_cost = job.token_cost
				jo.resourceCount = 0
				jo.competencyCount = 0
				jo.matrixCellCount = 0

				// compose string for worker status
				if (['not_started','queued','processing','failed','cancelled'].includes(job.worker_status)) {
					jo.worker_status_string = job.worker_status.toUpperCase()

					// add missing count to worker_stats if appropriate
					if (job.matrix_stats?.missingGenAICount > 0) {
						jo.worker_status += ` (${job.matrix_stats.missingGenAICount})`
					}

				} else {
					jo.worker_status_string = '—'
				}

				// compose string for job_description if we don't have one
				if (empty(jo.job_description)) {
					let ds = 'DESCRIPTION HERE'
					jo.job_description = ds
				}

				// compose string for counts
				let counts_string = '—'
				if (!empty(job.matrix_stats)) {
					jo.resourceCount = job.matrix_stats.resourceCount
					jo.competencyCount = job.matrix_stats.competencyCount
					jo.matrixCellCount = job.matrix_stats.matrixCellCount

					counts_string = 
						`Resources: ${job.matrix_stats.resourceCount} `
					  + `* Competencies: ${job.matrix_stats.competencyCount} `
					  + `= ${job.matrix_stats.matrixCellCount} `
					  + ` (${job.matrix_stats.missingGenAICount} missing)`
				}
				jo.counts_string = counts_string

				// compose string for ai stats
				let ai_stats = '—'
				if (!empty(job.token_cost)) {
					let seconds = job.genai_time_spent / 1000
					ai_stats = `$${job.token_cost.toFixed(2)}—${U.time_string(seconds)}`
					let genai_comparison_count = job.genai_comparison_count ?? job.matrix_stats?.matrixCellCount
					if (genai_comparison_count > 0) {
						let cell_cents = (job.token_cost / genai_comparison_count) * 100
						let cell_seconds = seconds / genai_comparison_count
						ai_stats += ` [${cell_cents.toFixed(3)}¢—${cell_seconds.toFixed(2)}s]`
					}
				}
				jo.ai_stats = ai_stats

				arr.push(jo)
			}
			return arr
		},

	},
	watch: {
		// params: { deep:true, immediate: true, handler() {
		// 	// console.warn('PARAMS WATCHER')
		// }},
		
		scram_manager_update_id() {
			// console.warn('scram_manager_update_id updated: ' + this.scram_manager_update_id)
			// this allows SCRAMJobView to trigger an update
			this.get_scram_jobs()
		},

	},
	created() {
	},
	async mounted() {
		vapp.scram_manager_component = this

		await this.get_scram_jobs()

		this.initialized = true
	},
	beforeDestroy() {
	},
	methods: {
		async get_scram_jobs() {
			let payload = {
				user_id: this.user_info.user_id,
				action: 'get_jobs',
			}

			U.loading_start()
			let result = await U.ajax_promise('align/scram', payload)
			U.loading_stop()

			if (result.status != 'ok') {
				this.$alert('ERROR: ' + result.status)
				return
			}

			this.jobs = result.jobs
		},

		edit_job(cram_job_id) {
			let params = {
				opened_from_embedded: false,
				cram_job_id: cram_job_id,
			}
			vapp.show_scram_job_view(params)
		},

		view_grid(cram_job_id) {
			window.open(`/crm/${cram_job_id}/grid/all/0`)
		},

		view_alignment_report(cram_job_id) {
			window.open(`/crm/${cram_job_id}/ar`)
		},

		create_new_job() {
			this.edit_job(0)
		},
	}
}
</script>

<style lang="scss">
.k-rcram-jobs-table {
	td {
		font-size:14px!important;
		line-height:17px!important;
	}

	.k-rcram-manager-id-cell {
		text-align:center;
	}

	.k-rcram-manager-description-cell {
		text-align:left;
	}

	.k-rcram-manager-creator-cell {
		text-align:center;
		font-size:12px!important;
	}

	.k-rcram-manager-count-cell {
		text-align:center;
	}

	.k-rcram-manager-status-cell {
		text-align:center;
		font-size:12px!important;
	}

	.k-rcram-manager-ai-cost-cell {
		text-align:center;
	}


	.k-rcram-manager-actions-cell {

	}
}
</style>
